import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Lightbulb, AlertTriangle, ShieldCheck, Dumbbell, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function LessonDetail() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/lessons/${lessonId}`).then((r) => setLesson(r.data.lesson)); }, [lessonId]);

  if (!lesson) return <div className="p-8 text-tc-muted font-mono text-sm">Loading lesson…</div>;
  const q = lesson.craft_check;

  const submit = async () => {
    if (selected == null) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/lessons/${lessonId}/complete`, { answer_index: selected });
      setAnswered(data);
      if (data.correct) toast.success(`Correct! +${data.xp_awarded} XP`);
      else toast.error("Not quite — review and try again.");
    } catch (e) { toast.error("Something went wrong"); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-5">
      <button onClick={() => navigate("/learn")} className="text-sm text-tc-muted hover:text-tc-txt flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Back to Learn</button>
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-mastery">Lesson</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1">{lesson.title}</h1>
      </div>

      <Section label="Why This Matters" accent="text-tc-coach">{lesson.why_this_matters}</Section>
      <div className="tc-card p-5 leading-relaxed text-tc-sub">{lesson.lesson}</div>

      <div className="grid sm:grid-cols-2 gap-4">
        <IconCard icon={Lightbulb} accent="text-tc-xp" title="Key Idea">{lesson.key_idea}</IconCard>
        <IconCard icon={AlertTriangle} accent="text-tc-bear" title="Common Mistake">{lesson.common_mistake}</IconCard>
        <IconCard icon={ShieldCheck} accent="text-tc-bull" title="Craft Rule">{lesson.craft_rule}</IconCard>
        <IconCard icon={Dumbbell} accent="text-tc-mastery" title="Mini Exercise">{lesson.mini_exercise}</IconCard>
      </div>

      {/* Craft Check quiz */}
      <div className="tc-card p-5" data-testid="craft-check">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach mb-2">Craft Check</div>
        <div className="font-medium mb-3">{q.question}</div>
        <div className="space-y-2">
          {q.options.map((o, i) => {
            const isSel = selected === i;
            const showRes = answered && (i === q.answer_index || isSel);
            const correct = answered && i === q.answer_index;
            return (
              <button key={i} data-testid={`quiz-option-${i}`} disabled={!!answered} onClick={() => setSelected(i)}
                className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm flex items-center justify-between transition-colors ${
                  showRes ? (correct ? "border-tc-bull/50 bg-tc-bull/10 text-tc-bull" : "border-tc-bear/50 bg-tc-bear/10 text-tc-bear")
                    : isSel ? "border-tc-coach bg-tc-coach/10" : "border-white/10 hover:border-white/25 text-tc-sub"}`}>
                {o}
                {showRes && (correct ? <CheckCircle2 className="w-4 h-4" /> : isSel ? <XCircle className="w-4 h-4" /> : null)}
              </button>
            );
          })}
        </div>
        {answered ? (
          <div className="mt-3 text-sm text-tc-sub bg-white/5 rounded-lg p-3">{answered.explanation}</div>
        ) : (
          <button data-testid="submit-quiz-btn" disabled={selected == null || busy} onClick={submit}
            className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg bg-tc-coach text-tc-bg font-semibold py-2.5 hover:bg-tc-coach/90 disabled:opacity-40">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit Answer
          </button>
        )}
        {answered && (
          <button onClick={() => navigate("/learn")} className="mt-3 w-full rounded-lg bg-white/5 border border-white/10 py-2.5 text-sm hover:bg-white/10">Back to Learn</button>
        )}
      </div>
    </div>
  );
}

function Section({ label, accent, children }) {
  return (
    <div className="tc-card p-5">
      <div className={`text-[11px] font-mono uppercase tracking-widest ${accent} mb-1.5`}>{label}</div>
      <div className="text-tc-sub leading-relaxed">{children}</div>
    </div>
  );
}
function IconCard({ icon: Icon, accent, title, children }) {
  return (
    <div className="tc-card p-4">
      <div className={`flex items-center gap-2 ${accent} font-heading font-semibold text-sm`}><Icon className="w-4 h-4" /> {title}</div>
      <div className="text-sm text-tc-sub mt-1.5 leading-relaxed">{children}</div>
    </div>
  );
}
